import { useState, useEffect, useCallback, useMemo } from "react";
import { COLOR_CODES } from "../../../common";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import { BrushStart } from "../../../models/chart_model";
import { RajasthanDiscomDrawal } from "../../../models/rajasthan";
import {
  getRajasthanDiscomDrawlData,
  getRajasthanDiscomDrawlForecastData,
} from "../Utility/RestApi";
import { R_CHART_TYPES } from "../const";
import { GnaRuvnlFooter } from "../components";
import { DynamicDatePicker } from "../../../components/DatePicker";
import {
  convertToString,
  formatDateRange,
  getMonthFromDate,
} from "../../../extensions/date";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { FilterTab } from "../../../components/Filters";
import { DataTabs, DEMAND_UNITS } from "./Data";
import { renderHourTick } from "../../../components/recharts/components";
import { generateDateRanges } from "../Generation/Data";
import { stringToConsistentColor } from "../../../extensions/string";
import Tooltip from "../../../components/tooltip";
import Papa, { Parser, ParseResult } from "papaparse";
import { DEFAULT_DATES } from "../../Hppc/Demand/Data";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useDispatch, useSelector } from "react-redux";
import { ExchangeColors } from "../Exchange3/FormatData";
import { AddDatesWithFileName } from "../../../helpers/helper";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { RajasthanDrawalPeakAnalysis } from "./Drawal_TrendPeak";
import { RajasthanDrawalSummaryAnalysis } from "./Drawal_TrendSummary";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
const GROUP_BY = [
  {
    label: "Area",
    key: "area",
    values: ["area", "discom"],
  },

  {
    label: "Discom",
    key: "discom",
    values: ["discom"],
  },
  {
    label: "District",
    key: "district",
    values: ["district", "district_only"],
  },
  {
    label: "Total",
    key: "total",
    values: ["total"],
  },
];

const DISCOM = [
  { label: "Jodhpur", key: "Jodhpur", apiKey: "JDVVNL" },
  { label: "Jaipur", key: "Jaipur", apiKey: "JVVNL" },
  { label: "Ajmer", key: "Ajmer", apiKey: "AVVNL" },
];

// Helper function to convert display names to API keys
function getApiDiscomKeys(displayKeys: string[]): string[] {
  return displayKeys.map((displayKey) => {
    const discom = DISCOM.find((d) => d.key === displayKey);
    return discom ? discom.apiKey : displayKey;
  });
}

// Helper function to convert API keys to display names
function getDisplayDiscomName(apiKey: string): string {
  const discom = DISCOM.find((d) => d.apiKey === apiKey);
  return discom ? discom.label : apiKey;
}

let Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "dateIndex"],
    isTime: true,
  },
  {
    label: "Date",
    key: "dateIndex",
    values: ["dateIndex"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = DEMAND_UNITS;

export function RajasthanDrawalTrend() {
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const uniqueDate = DefaultDates[activeTab].trendDateOptions;
  const dataTab = useSelector((state: any) => state.viewMode.activeView);

  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [data, setData] = useState<any[]>([]);
  const [fileForecastData, setFileForecastData] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([GROUP_BY[1]]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [loading, setLoading] = useState(true);

  // Handler for file upload—parses CSV with expected columns: date, block, area, predicted_demand.
  function handleForecastFile(e: React.ChangeEvent<HTMLInputElement>) {
    const forecastFile = e.target.files?.[0];
    e.target.value = ""; // reset file input
    let allData: any[] = [];
    setLoading(true);
    if (!forecastFile) return;
    Papa.parse(forecastFile, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      worker: true,
      chunk: (results: ParseResult<any>, parser: Parser) => {
        const chunkData = results.data.map((row: any) => ({
          date: row.date,
          block: row.block,
          area: row.area,
          predicted_demand: parseFloat(row.predicted_demand),
          mw: parseFloat(row.predicted_demand),
          mwh: parseFloat(row.predicted_demand) / 4,
          lu: parseFloat(row.predicted_demand) / 400,
          discom: "Total",
          month: new Date(row.date).getMonth() + 1,
        }));
        allData = allData.concat(chunkData);
      },
      complete: () => {
        setFileForecastData(allData);
        setUniqueBlock(Array.from(new Set(allData.map((item) => item.block))));
        setLoading(false);
      },
      error: (error: any) => {
        console.error("Error parsing forecast CSV:", error);
        setLoading(false);
      },
    });
  }

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultTrendDates === undefined) {
      return;
    }

    setLoading(true);

    const dates = DefaultDates[activeTab].defaultTrendDates;
    const promises = dates.map(async (date: any) => {
      const [startDate, endDate, model] = date.value.split(",");
      if (model) {
        const forecastedData = await getRajasthanDiscomDrawlForecastData({
          startDate,
          endDate,
        });
        return forecastedData.map((item: any) => ({
          ...item,
          dates: date.value,
        }));
      }
      const rangeData = await getRajasthanDiscomDrawlData({
        startDate,
        endDate,
      });

      return rangeData.map((item: any) => ({
        ...item,
        dates: date.label,
      }));
    });
    const results = await Promise.all(promises);
    const newData = results.flat();
    console.log("🚀 ~ const_handlefetchData=useCallback ~ newData:", newData);
    setData(newData);
    setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
    setLoading(false);
  }, [activeTab, DefaultDates]);

  async function getForecastData(startDate: string, endDate: string) {
    setLoading(true);

    const fdata = fileForecastData.filter((item) => {
      const date_num = parseInt(item.date.replace(/-/g, ""));
      return (
        date_num >= parseInt(startDate.replace(/-/g, "")) &&
        date_num <= parseInt(endDate.replace(/-/g, ""))
      );
    });

    const uniqueDates = Array.from(new Set(fdata.map((item) => item.date)));

    const final = fdata.map((item: any) => ({
      ...item,
      dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
      dates: `${startDate} to ${endDate}`,
      block: item.block,
      month: getMonthFromDate(item.date),
    }));

    final.sort((a, b) => {
      const dateComparison = a.date.localeCompare(b.date);
      if (dateComparison !== 0) {
        return dateComparison;
      }
      if (a.block < b.block) return -1;
      if (a.block > b.block) return 1;
      return 0;
    });

    return final;
  }

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys([GROUP_BY[index]]);
    setSelectedDistricts([]);
    setSelectedArea([]);
  }

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function handleUnitChange(params: string) {
    setSelectedUnit(params);
  }

  function handleDiscomChange(items: string[]) {
    setSelectedDiscom(items);
  }

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  // ─── data processing (Optimized with useMemo) ─────────────────────────
  const processedData = useMemo(() => {
    return data.map((item) => {
      let monthStr = "";
      if (item.date) {
        const date = new Date(item.date);
        const shortMonth = date.toLocaleDateString("en-US", { month: "short" });
        const shortYear = date.getFullYear().toString().slice(-2);
        monthStr = `${shortMonth}-${shortYear}`;
      }
      return {
        ...item,
        mwh: item.mw / 4,
        lu: item.mw / 400,
        month: monthStr || item.month,
      };
    });
  }, [data]);

  const discomFilteredData = useMemo(() => {
    if (selectedDiscom.length === 0) return processedData;
    return processedData.filter((item) => selectedDiscom.includes(item.discom));
  }, [processedData, selectedDiscom]);

  const uniqueDistrict = useMemo(() => {
    return Array.from(
      new Set(
        discomFilteredData.map((item) => {
          const name = item.district || item.area;
          const discomName = getDisplayDiscomName(item.discom);
          return name ? `${name} (${discomName})` : "";
        }),
      ),
    )
      .filter((label) => label !== "" && !label.includes("undefined"))
      .sort();
  }, [discomFilteredData]);

  const districtFilteredData = useMemo(() => {
    if (selectedDistricts.length === 0) return discomFilteredData;
    const distStrings = selectedDistricts.map((d: any) => d.value);
    return discomFilteredData.filter((item) => {
      const name = item.district || item.area;
      const discomName = getDisplayDiscomName(item.discom);
      return distStrings.includes(`${name} (${discomName})`);
    });
  }, [discomFilteredData, selectedDistricts]);

  const uniqueArea = useMemo(() => {
    return Array.from(
      new Set(
        districtFilteredData.map((item) => {
          const name = item.area || item.station;
          const discomName = getDisplayDiscomName(item.discom);
          return name ? `${name} (${discomName})` : "";
        }),
      ),
    )
      .filter((label) => label !== "" && !label.includes("undefined"))
      .sort();
  }, [districtFilteredData]);

  const { chartData, TableData, chartKeys } = useMemo(() => {
    let res = districtFilteredData;
    if (selectedArea.length > 0) {
      const areaStrings = selectedArea.map((a: any) => a.value);
      res = res.filter((item) => {
        const name = item.area || item.station;
        const discomName = getDisplayDiscomName(item.discom);
        return areaStrings.includes(`${name} (${discomName})`);
      });
    }

    if (selectedBlock.length > 0) {
      const blockValues = selectedBlock.map((opt) => opt.value);
      res = res.filter((item) => blockValues.includes(item.block));
    }

    const par: ChartDataParams = {
      keys:
        selectedInterval.key === "dateIndex"
          ? ["dateIndex"]
          : selectedInterval.values,
      tableKeys: ["dateIndex", "block", "month"],
      groupBy: ["dates"].concat(
        selectedArea.length > 0
          ? ["area"]
          : selectedDistricts.length > 0
            ? ["district"]
            : selectedGroupKeys.map((item) => item.key.toLowerCase()),
      ),
      volumeKey: "mwh",
      aggregationKeys: ["lu"],
      averageKeys: ["mw"],
      averageDuplicationKeys: ["dateIndex", "block"],
      priceKey: "",
      data: res,
    };
    const cData = FormatChartData(par);
    return {
      chartData: cData.data || [],
      TableData: cData.tableData || [],
      chartKeys: cData.keys,
    };
  }, [
    districtFilteredData,
    selectedArea,
    selectedBlock,
    selectedInterval,
    selectedGroupKeys,
    selectedDistricts.length,
  ]);

  const districtOptions = useMemo(() => {
    return uniqueDistrict.map((item) => ({
      value: item,
      label: item,
    }));
  }, [uniqueDistrict]);

  const areaOptions = useMemo(() => {
    return uniqueArea.map((item) => ({
      value: item,
      label: item,
    }));
  }, [uniqueArea]);

  // useEffect(() => {
  //   if (selectedLegends.length > 0) {
  //     const selectedLegendNames = selectedLegends.map((legend) => legend.name);

  //     let aa = [];
  //     let bb: any = [];
  //     for (let i = 0; i < selectedLegendNames.length; i++) {
  //       for (let j = 0; j < data.length; j++) {
  //         if (selectedLegendNames[i] === data[j].district) {
  //           aa.push(data[j]);
  //         }
  //       }
  //     }

  //     for (let i = 0; i < aa.length; i++) {
  //       const discomName = getDisplayDiscomName(aa[i].discom);
  //       if (bb.includes(aa[i].area + " (" + discomName + ")")) {
  //         continue;
  //       } else {
  //         bb.push(aa[i].area + " (" + discomName + ")");
  //       }
  //     }

  //     setUniqueArea(bb);
  //   }
  // }, [data, selectedLegends]);

  const legends = useMemo(() => {
    const getName = (key: string) => {
      let name = key;
      const datePart = key.split("_")[0];
      const isDate = /^\d{4}-\d{2}-\d{2} to \d{4}-\d{2}-\d{2}/.test(datePart);

      // If key contains "_", second part is area/district/discom
      if (key.includes("_")) {
        name = key.split("_")[1] || key.split("_")[0];
      }

      // Remove date range prefix if it's there in the name part
      name = name.replace(/^\d{4}-\d{2}-\d{2} to \d{4}-\d{2}-\d{2}\s*/, "");

      // Remove discom suffix "(...)"
      name = name.replace(/ \(.*?\)$/, "");

      // Handle undefined or empty values or "total"
      if (
        name.toLowerCase() === "undefined" ||
        name.trim() === "" ||
        name.toLowerCase() === "total"
      ) {
        name = "Demand";
      }

      if (isDate) {
        return `${datePart} ${name}`.trim();
      }

      return name.trim();
    };

    return chartKeys.map((key) => ({
      dataKey: `${key}_${selectedUnit.toLowerCase()}`,
      name: getName(key),
      legendColor: undefined,
      stroke: undefined,
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined
          ? selectedChartType.stackId
          : undefined,
    }));
  }, [chartKeys, selectedUnit, selectedChartType]);

  function summaryExtraKeys() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        if (selectedInterval.key === "month") {
          return ["month"];
        } else {
          return ["dateIndex", "block"];
        }
      } else {
        return ["dateIndex", "block"];
      }
    } else {
      return [selectedInterval.values[0]];
    }
  }

  function summaryExtraLabels() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        if (selectedInterval.key === "month") {
          return ["Month"];
        } else {
          return ["Date Index", "Block"];
        }
      } else {
        return ["Date Index", "Block"];
      }
    } else {
      return [selectedInterval.values[0]];
    }
  }

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      if (selectedBlock.length > 0) {
        return "block";
      }
      return "block";
    } else {
      return selectedInterval.key;
    }
  }, [selectedBlock, selectedInterval]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);
  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends, [chartData]);

  const commonSummaryProps = {
    legends: visibleLegends as any,
    data: chartData.map((item) => ({
      ...item,
      dateIndex:
        item.dateIndex && typeof item.dateIndex === "string"
          ? parseInt(item.dateIndex.replace("Day", ""))
          : item.dateIndex || 0,
    })),
    tableData: TableData.map((item) => ({
      ...item,
      dateIndex:
        item.dateIndex && typeof item.dateIndex === "string"
          ? parseInt(item.dateIndex.replace("Day", ""))
          : item.dateIndex || 0,
    })),
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels(),
    filename: AddDatesWithFileName({
      fileName: "Restricted Demand Trend Analysis",
      dates: DefaultDates[activeTab].defaultTrendDates,
    }),
  };

  // Show Peak Analysis component when selected
  if (selectedXAxisMode.value === "peak") {
    return (
      <RajasthanDrawalPeakAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        selectedGroupKeys={selectedGroupKeys}
        onGroupByChange={_handleGroupByChange}
        selectedArea={selectedArea}
        onAreaChange={setSelectedArea}
        selectedDistricts={selectedDistricts}
        onDistrictsChange={setSelectedDistricts}
      />
    );
  }

  // Show Summary component when selected
  if (selectedXAxisMode.value === "summary") {
    return (
      <RajasthanDrawalSummaryAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        selectedGroupKeys={selectedGroupKeys}
        onGroupByChange={_handleGroupByChange}
        selectedArea={selectedArea}
        onAreaChange={setSelectedArea}
        selectedDistricts={selectedDistricts}
        onDistrictsChange={setSelectedDistricts}
      />
    );
  }

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />
          <FilterMenu
            title="Interval"
            items={Intervals.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => setSelectedInterval(Intervals[index])}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => handleUnitChange(item)}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={setSelectedDiscom}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.label)}
            selectedItems={selectedGroupKeys.map((item) => item.label)}
            onSelect={(item, index) => _handleGroupByChange(index)}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(R_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">
              Restricted Demand ({selectedUnit})
            </h2>
            <div className="flex gap-x-6  justify-end">
              {selectedGroupKeys[0]?.key === "district" && (
                <div className="min-w-40">
                  <SearchBox
                    options={districtOptions}
                    isMany={true}
                    placeholder="Search District"
                    selectedOptions={selectedDistricts}
                    onChange={(v: any) => {
                      setSelectedDistricts(v);
                      setSelectedArea([]); // Reset area when district changes
                    }}
                  />
                </div>
              )}
              {(selectedGroupKeys[0]?.key === "area" ||
                (selectedGroupKeys[0]?.key === "district" &&
                  selectedDistricts.length > 0)) && (
                <div className="min-w-40">
                  <SearchBox
                    options={areaOptions}
                    isMany={true}
                    placeholder="Search Area"
                    selectedOptions={selectedArea}
                    onChange={(v: any) => {
                      setSelectedArea(v);
                    }}
                  />
                </div>
              )}
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />
              <button
                onClick={() => {
                  if (dataTab.label === "Chart") {
                    downloadChartTableCSV(commonSummaryProps);
                  }
                  if (dataTab.label === "Summary") {
                    downloadSummaryCSV(commonSummaryProps);
                  }
                  if (dataTab.label === "Table") {
                    downloadChartTableCSV(commonSummaryProps);
                  }
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.Start}
              isTimeSlot={selectedInterval.isTime}
              onlyTitle={
                selectedBlock.length !== 0 &&
                selectedInterval.key == "dateIndex"
              }
              barCategoryGap={selectedInterval.isTime ? 2 : 40}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              legends={legends}
              onLegendSelect={handleLegendSelect}
              defaultSelectedLegends={selectedLegends}
              xDataKey={xDataKey}
              xTick={
                selectedInterval.isTime && selectedBlock.length === 0
                  ? (value: any, index: any) => renderHourTick(value, 10)
                  : undefined
              }
              secondXDataKey={
                selectedInterval.isTime && selectedBlock.length > 0
                  ? "dateIndex"
                  : selectedInterval.values[1]
              }
            />
          )}
          {dataTab.label === "Summary" && (
            <LegendSummaryTable {...commonSummaryProps} />
          )}
          {dataTab.label === "Table" && <ChartTable {...commonSummaryProps} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>GSS Report (RUVITL)</p>}
              text="1. Sum of all GSS load = Actual drawal (Restricted demand) ..."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}